import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router"
import { INDEX_PATH } from "./api"
import { openCompanyQueue } from "./harvestBridge"
import { operations, type DiscoveryJob } from "./operationsApi"
const statuses: Record<DiscoveryJob["status"], TranslationKeys> = {
  queued: "CI_JOB_WAITING",
  running: "CI_JOB_RUNNING",
  completed: "CI_JOB_COMPLETED",
  partial: "CI_JOB_PARTIAL",
  failed: "CI_SOURCE_FAILED",
  cancelled: "CI_JOB_CANCELLED",
}
export function OperationsPanel() {
  const { user, hasPermission } = useUser()
  const { translate: t } = useTranslation()
  const cache = useQueryClient()
  const key = ["company-index", user?.organization, "operations"]
  const query = useQuery({
    queryKey: key,
    queryFn: operations.list,
    refetchInterval: 10000,
  })
  const refresh = () => {
    void cache.invalidateQueries({ queryKey: key })
  }
  const acknowledge = useMutation({
    mutationFn: operations.acknowledge,
    onSuccess: refresh,
  })
  const cancel = useMutation({
    mutationFn: operations.cancel,
    onSuccess: refresh,
  })
  const open = useMutation({ mutationFn: openCompanyQueue })
  const canEdit = hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD)
  return (
    <section className="space-y-4 rounded-xl border p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-semibold">{t("CI_OPERATIONS")}</h2>
        {canEdit && (
          <Button disabled={open.isPending} onClick={() => open.mutate()}>
            {t("CI_OPEN_RUNNER")}
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground">{t("CI_RUNNER_HINT")}</p>
      {open.isSuccess && <p role="status">{t("CI_RUNNER_OPENED")}</p>}
      {open.isError && <p role="alert">{open.error.message}</p>}
      {(acknowledge.isError || cancel.isError) && (
        <p role="alert">{t("CI_OPERATIONS_ERROR")}</p>
      )}
      {query.isPending && <p>{t("CI_LOADING")}</p>}
      {query.isError && (
        <p role="alert">
          {t("CI_ERROR")}{" "}
          <Button onClick={() => void query.refetch()}>{t("CI_RETRY")}</Button>
        </p>
      )}
      {query.data && (
        <>
          <h3 className="font-medium">{t("CI_NOTIFICATIONS")}</h3>
          {!query.data.notifications.length && (
            <p className="text-muted-foreground">
              {t("CI_NOTIFICATIONS_EMPTY")}
            </p>
          )}
          <ul className="space-y-2 max-h-80 overflow-auto">
            {query.data.notifications.map((item) => (
              <li key={item.id} className="space-y-1 rounded border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Link
                    className="font-medium underline"
                    to={`${INDEX_PATH}/${item.companyId}`}
                  >
                    {item.companyName}
                  </Link>
                  {item.read ? (
                    <span className="text-xs text-muted-foreground">
                      {t("CI_ACKNOWLEDGED")}
                    </span>
                  ) : (
                    canEdit && (
                      <Button
                        variant="outline"
                        disabled={acknowledge.isPending}
                        onClick={() => acknowledge.mutate(item.id)}
                      >
                        {t("CI_ACKNOWLEDGE")}
                      </Button>
                    )
                  )}
                </div>
                <p>{item.message}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(item.createdAt).toLocaleString()}
                </p>
              </li>
            ))}
          </ul>
          <h3 className="font-medium">{t("CI_DISCOVERY_JOBS")}</h3>
          <p className="text-xs text-muted-foreground">
            {t("CI_RECENT_OPERATIONS")}
          </p>
          {!query.data.jobs.length && (
            <p className="text-muted-foreground">{t("CI_JOBS_EMPTY")}</p>
          )}
          <ul className="space-y-2 max-h-96 overflow-auto">
            {query.data.jobs.map((job) => (
              <li key={job.id} className="space-y-2 rounded border p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Link
                    className="font-medium underline"
                    to={`${INDEX_PATH}/${job.companyId}`}
                  >
                    {job.companyName}
                  </Link>
                  <span>
                    {t(statuses[job.status])} ·{" "}
                    {t(
                      job.kind === "repair"
                        ? "CI_REPAIR_JOB"
                        : "CI_DISCOVERY_JOB",
                    )}
                  </span>
                </div>
                <p className="text-xs break-all text-muted-foreground">
                  {job.url}
                </p>
                <p className="text-sm">
                  {t("CI_SAVED_FILES")}: {job.candidateIds.length} ·{" "}
                  {new Date(job.createdAt).toLocaleString()}
                </p>
                {job.errorCode && (
                  <p className="text-sm">
                    {job.errorCode.replaceAll("_", " ")}
                  </p>
                )}
                {canEdit && ["queued", "running"].includes(job.status) && (
                  <Button
                    variant="outline"
                    disabled={cancel.isPending}
                    onClick={() => cancel.mutate(job.id)}
                  >
                    {t("CI_CANCEL_JOB")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}
